// Everything project-specific about the chatbot lives here. To reuse the widget
// in another project, copy the ArkChatbot folder and edit only this file (see
// README.md in this folder).

export type ChatHistoryItem = { role: 'user' | 'bot'; text: string };

export interface ChatRequestContext {
  sessionId: string;
  history: ChatHistoryItem[];
}

export interface BotReply {
  text: string;
  buttons?: string[];
}

export const CHATBOT_CONFIG = {
  // Header: title with a small subtitle under it
  headerTitle: 'Support',
  headerSubtitle: 'Ask me anything', // empty = hidden
  title: 'Support', // bot name on its messages ("Support at 3:47 PM, …")
  // Robot shown in the header, greeting and beside replies (empty = "A" orb)
  avatarUrl: `${process.env.PUBLIC_URL}/static/images/chatbot/ark-bot-2.png`,  statusText: '', // green-dot status in the header, e.g. 'Online' (empty = hidden)
  primaryColor: '#492728',
  // Small line under the input (empty = hidden)
  footerText: '',

  // Greeting shown until the first question: "Hi <name>!", then
  // "I can help you with" followed by these topics typed out in turn.
  greetingTopics: [
    'claim explanations.',
    'your documents.',
    'reviewing content.',
    'finding answers fast.'
  ],
  // Optional bot bubbles under the greeting (Markdown supported)
  welcomeMessages: [] as string[],
  // "Try asking" cards under the greeting; clicking one sends it (empty = none)
  suggestions: [
    'What is a claim?',
    'How do I upload a document?',
    'How are claims reviewed?'
  ],
  placeholder: 'Type a message...',
  waitingPlaceholder: 'Waiting for response...',
  // Friendly replies for each kind of failure (Markdown supported).
  errorMessages: {
    offline: 'You appear to be offline. Please check your connection and try again.',
    timeout: 'The request timed out. Please try again.',
    unavailable: 'Unable to proceed with your request, please try again later.',
    signIn: 'Please sign in to continue.',
    noAnswer: 'No answer was found. Please rephrase your question.',
    generic: 'Your request could not be processed. Please try again.'
  },

  // Socket.IO chat server, reached at wss://arkapistag.inabia.com/chat. The
  // "/chat" is the proxy route, so it goes in `socketPath` (a "/chat" on the
  // URL would be read as a Socket.IO namespace and fail).
  // REACT_APP_CHATBOT_SOCKET_URL / _PATH in .env override these at build time.
  socketUrl:
    process.env.REACT_APP_CHATBOT_SOCKET_URL || 'wss://arkapistag.inabia.com',
  socketPath:
    process.env.REACT_APP_CHATBOT_SOCKET_PATH || '/chat/socket.io',
  sendEvent: 'user_message', // event the question is emitted on
  replyEvent: 'ai_message', // event the answer arrives on
  // Longer than the server's own model timeout (stag_rag TIMEOUT = 180s), so a
  // slow answer still arrives instead of the widget giving up first.
  timeoutMs: 200000,

  // Keep the conversation across page reloads (cleared on logout because
  // logout wipes localStorage).
  persist: true,
  storageKey: 'ark-chatbot'
};

// Payload emitted on `sendEvent`. Chat server contract (server.py):
//   client -> server  "user_message"  {"message": "<text>"}
//   server -> client  "ai_message"    {"message": "<text>"}
export const buildRequest = (
  query: string,
  _ctx: ChatRequestContext
): unknown => ({ message: query });

const pickText = (obj: any): string | undefined => {
  if (typeof obj === 'string') return obj;
  if (!obj || typeof obj !== 'object') return undefined;
  for (const key of ['response', 'answer', 'message', 'text', 'output', 'result']) {
    const value = obj[key];
    if (typeof value === 'string') return value;
    if (value && typeof value === 'object') {
      const nested = pickText(value);
      if (nested) return nested;
    }
  }
  return undefined;
};

const pickButtons = (obj: any): string[] | undefined => {
  if (!obj || typeof obj !== 'object') return undefined;
  const raw = obj.buttons ?? obj.suggestions ?? obj.choices;
  if (!Array.isArray(raw)) return undefined;
  const labels = raw
    .map((b: any) =>
      typeof b === 'string' ? b : b?.label ?? b?.name ?? b?.text ?? b?.title
    )
    .filter((b: unknown): b is string => typeof b === 'string' && !!b.trim());
  return labels.length ? labels : undefined;
};

// Turn the API response into one or more bot messages. Accepts a plain string,
// { response | answer | message | text: '...' }, a { data: ... } wrapper, or
// { messages: [...] } for multi-bubble replies. Optional `buttons` /
// `suggestions` / `choices` render as quick replies.
export const parseResponse = (data: any): BotReply[] => {
  // Socket replies may arrive as a JSON string, e.g. '{"message":"..."}'.
  if (typeof data === 'string' && /^\s*[[{]/.test(data)) {
    try {
      return parseResponse(JSON.parse(data));
    } catch {
      // Not JSON — treat it as plain text below.
    }
  }
  if (data && typeof data === 'object' && !Array.isArray(data) && data.data) {
    const inner = parseResponse(data.data);
    if (inner.length) return inner;
  }

  const list = Array.isArray(data)
    ? data
    : Array.isArray(data?.messages)
    ? data.messages
    : null;

  if (list) {
    const replies = list
      .map((item: any) => ({ text: pickText(item) ?? '', buttons: pickButtons(item) }))
      .filter((r: BotReply) => r.text || r.buttons);
    const trailingButtons = pickButtons(data);
    if (trailingButtons && replies.length) {
      replies[replies.length - 1].buttons = trailingButtons;
    }
    return replies;
  }

  const text = pickText(data);
  const buttons = pickButtons(data);
  return text || buttons ? [{ text: text ?? '', buttons }] : [];
};

// Pick the friendly message for a failed request.
export const errorMessageFor = (err: any): string => {
  const m = CHATBOT_CONFIG.errorMessages;
  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    return m.offline;
  }
  if (err?.message === 'timeout') return m.timeout;
  // Chat server unreachable or connection dropped
  if (err?.message === 'unavailable') return m.unavailable;
  return m.generic;
};
